<template>
  <div class="subscription-licences-list">
    <headline>
      {{ $tc('subscription.licence', 2) }}
    </headline>

    <v-row >
      <v-col>
        <v-row>
          <v-col>
            <v-text-field
                v-model="search"
                :label="$t('search')"
                prepend-icon="mdi-filter"
                hide-details
                clearable
            />
          </v-col>
          <v-col  v-if="$slots.filters">
            <slot name="filters"></slot>
          </v-col>
          <v-col cols="auto" v-if="licenceInactivesCount > 0">
            <v-select
                v-model="showActiveLicence"
                hide-details
                :items="[
                  {text: $t('active'), value: true},
                  {text: $t('inactive'), value: false},
                ]"
            ></v-select>
          </v-col>
        </v-row>
      </v-col>
      <v-col cols="auto" class="text-right d-flex align-center">
        <v-btn small @click="openAddLicenceForm">
          <v-icon left>mdi-plus</v-icon>
          {{ $t('subscription.add-licence')}}
        </v-btn>
        <slot name="actions"></slot>
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <v-data-table
          :headers="headers"
          :items="licencesFiltered"
          :loading="loading"
          :options="{
            sortBy: [],
            sortDesc: [],
          }"
        >
          <template #item.name="{item}">
            {{ item.subscriptionPlan.name }}
          </template>

          <template #item.config="{item}">
            <div>
              <v-chip v-if="item.formula" class="mb-2 mr-2" color="blue" dark small>{{ item.formula.name }}</v-chip>
              <v-chip class="mb-2 mr-2" color="orange" dark small v-for="option in item.options" :key="option.id">{{ option.name }}</v-chip>
              <v-chip class="mb-2 mr-2" v-if="item.isExemptFromBilling" outlined color="red" dark small>{{ $t('subscription.not-invoiced') }}</v-chip>
              <template v-if="item.quantity > 1">({{ $tc('subscription.licence-units-count-display', item.quantity, {quantity: item.quantity}) }})</template>
            </div>
<!--            <v-chip class="mb-2 mr-2" color="grey" dark small v-for="property in itemProperties(item)" :key="property.value">{{ property.text }}</v-chip>-->
<!--            <v-chip class="mb-2 mr-2" color="blue" dark small v-for="filter in itemFilters(item)" :key="filter.value">{{ filter.text }}</v-chip>-->
          </template>

          <template #item.activationStartDate="{value}">
            {{ value | formatDate($t('format_date')) }}
          </template>



          <template #item.price="{item}">
            <template v-if="item.isExemptFromBilling || !item.price">-</template>
            <template v-else>{{ item.price }} €</template>
          </template>

          <template #item.subscribedAt="{value}">
            {{ value | formatDate($t('format_date')) }}
          </template>

          <template #item.billingStartDate="{value}">
            {{ value | formatDate($t('format_date')) }}
          </template>

          <template #item.unsubscribedAt="{item}">
            <template v-if="item.unsubscribedAt">
              <v-chip v-if="item.active" small color="orange" outlined>
                <v-icon left small>mdi-calendar-clock</v-icon>{{ item.unsubscribedAt | formatDate($t('format_date')) }}
              </v-chip>
              <template v-else>{{ item.unsubscribedAt | formatDate($t('format_date')) }}</template>
            </template>
            <template v-else>—</template>
          </template>

          <template #item.related.label="{item}">
            <v-icon left v-if="item.related?.type === 'member'">ico-carplug</v-icon>
            <v-icon left v-if="item.related?.type === 'organization'">ico-organization-menu</v-icon>
            <v-icon left v-if="item.related?.type === 'charging-point'">ico-menu-charging-point</v-icon>
            <v-icon left v-if="item.related?.type === 'meter-device'">mdi-counter</v-icon>
            <v-icon left v-if="item.related?.type === 'vehicle'">mdi-car</v-icon>

            <router-link v-if="item.related?.type === 'charging-point'" :to="{name: 'charging-points.detail', params: {id: item.related.id}}">
              {{ item.related.label }}
            </router-link>

            <router-link v-if="item.related?.type === 'meter-device'" :to="{name: 'meter-device.detail', params: {id: item.related.id}}">
              {{ item.related.label }}
            </router-link>
            <router-link v-if="item.related?.type === 'organization'" :to="{name: 'organizations.detail', params: {id: item.related.id}}">
              {{ item.related.label }}
            </router-link>
            <router-link v-if="item.related?.type === 'member'" :to="{name: 'member', params: {id: item.related.id}}">
              {{ item.related.label }}
            </router-link>
            <router-link v-if="item.related?.type === 'vehicle'" :to="{name: 'vehicle.detail', params: {id: item.related.id}}">
              {{ item.related.label }}
            </router-link>

            <span class="font-weight-light" v-if="item.related?.members?.length > 0">
              (<span v-for="member in item.related.members" :key="member.id">
                <router-link  :to="{name: 'member', params: {memberId: member.id}}">
                  {{ member.name }}
                </router-link>
              </span>)
            </span>

          </template>

          <template #item.active="{value}">
            <v-icon v-if="value" color="green">mdi-check</v-icon>
            <v-icon v-else color="red">mdi-close</v-icon>
          </template>

          <template #item.actions="{item}">
            <audit-trail-button entity-type="SubscriptionLicence" :entity-id="item.id" />
            <v-btn v-if="item.active" small icon @click="openEditLicenceForm(item)">
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
            <!-- Résiliation future programmée sur une licence encore active : modifier la date ou l'annuler. -->
            <template v-if="item.active && item.unsubscribedAt">
              <v-btn small icon :title="$t('subscription.edit-terminate-date')" @click="openUnsubscribeModalFor(item)">
                <v-icon>mdi-calendar-edit</v-icon>
              </v-btn>
              <v-btn small icon color="orange" :title="$t('subscription.cancel-unsubscribe')" @click="cancelUnsubscription(item)">
                <v-icon>mdi-calendar-remove</v-icon>
              </v-btn>
            </template>
            <v-btn v-if="!item.active" small icon @click="cancelUnsubscription(item)">
              {{ $t('subscription.cancel-unsubscribe') }}
            </v-btn>
          </template>


        </v-data-table>
      </v-col>
    </v-row>

    <v-dialog v-model="showUnsubscribeModal" max-width="600">
      <v-card>
        <v-card-title>
          {{ unsubscribeIsModify ? $t('subscription.edit-terminate-date') : $t('subscription.terminate-a-licence') }}
        </v-card-title>
        <v-card-text>
          <form-field
            type="date"
            :label="$t('subscription.terminate-date')"
            v-model="unsubscribeDate"
          ></form-field>
        </v-card-text>
        <v-card-actions>
          <v-row>
            <v-col class="text-right">
              <v-btn class="mr-3" small @click="showUnsubscribeModal = false">{{ $t('cancel') }}</v-btn>
              <v-btn small color="primary" @click="unsubscribe(unsubscribeLicenceId, unsubscribeDate)">{{ unsubscribeIsModify ? $t('subscription.edit-terminate-date') : $t('subscription.terminate-a-licence') }}</v-btn>
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showLicenceFormModal" max-width="1000" scrollable>
      <v-form ref="form" @submit.prevent="saveLicence" v-if="addLicenceForm">
        <v-card>
          <v-card-title>
            {{ $t(isEditLicence ?  'subscription.edit-licence' : 'subscription.add-licence') }}
          </v-card-title>
          <v-card-text>
            <v-row>
              <v-col>
                <v-select
                  :label="$tc('subscription.subscription')"
                  v-model="addLicenceForm.subscriptionId"
                  :items="subscriptions"
                  item-value="id"
                  item-text="name"
                  hide-details
                  :disabled="isEditLicence"
                ></v-select>
              </v-col>
            </v-row>

            <template v-if="subscription">
              <v-row v-if="['device', 'charging-point', 'energy-provider'].includes(subscription.licenceType)">
                <v-col cols="12">
                  <template v-if="!isEditLicence">

                    <!-- Ouvert depuis une borne ou un compteur : le type est celui de la fiche, pas à choisir -->
                    <v-radio-group v-if="!chargingPointId && !meterDeviceId" v-model="selectedDeviceType">
                      <v-row>
                        <v-col cols="auto">
                          <v-radio
                              :label="$t('charging-point.charging-point')"
                              value="charging-point"
                          />
                        </v-col>
                        <v-col cols="auto">
                          <v-radio
                              :label="$tc('meter-device.meter-device')"
                              value="meter-device"
                          />
                        </v-col>
                      </v-row>
                    </v-radio-group>

                    <charging-point-select
                        v-if="selectedDeviceType === 'charging-point'"
                        :label="$t('charging-point.charging-point')"
                        v-model="chargingPointIds"
                        multiple
                        :limit="-1"
                        :filter-query="{
                          organizationId: subscription.organizationId,
                        }"
                        :exclude="excludeChargingPointIds"
                        :disabled="!!chargingPointId"
                    />
                    <meter-device-select
                        v-if="selectedDeviceType === 'meter-device'"
                        :label="$tc('meter-device.meter-device', 2)"
                        v-model="meterDeviceIds"
                        multiple
                        :limit="-1"
                        :filter-query="meterSelectFilterQuery"
                        :exclude="excludeMeterDeviceIds"
                        :disabled="!!meterDeviceId"
                    />
                  </template>
                  <v-text-field
                      v-else
                      :label="addLicenceForm.related?.type === 'meter-device' ? $tc('meter-device.meter-device') : $t('charging-point.charging-point')"
                      :value="addLicenceForm.related?.label"
                      hide-details
                      disabled
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-row v-if="['organization'].includes(subscription.licenceType)">
                <v-col cols="12">
                  <organization-select
                      v-model="addLicenceForm.organizationId"
                  />
                </v-col>
              </v-row>
              <v-row v-if="subscription.licenceType === 'vehicle'">
                <v-col cols="12">
                  <vehicle-select
                      v-if="!isEditLicence"
                      :label="$tc('vehicle.vehicle')"
                      v-model="addLicenceForm.vehicleId"
                      :organization-id="subscription.organizationId"
                      :exclude="excludeVehicleIds"
                  />
                  <v-text-field
                      v-else
                      :label="$tc('vehicle.vehicle')"
                      :value="addLicenceForm.related?.label"
                      hide-details
                      disabled
                  />
                </v-col>
              </v-row>


              <v-row v-if="isEditLicence">
                <v-col cols="12">
                  <v-text-field
                      type="number"
                      :label="$t('subscription.licence-units-count')"
                      v-model.number="addLicenceForm.quantity"
                  />
                </v-col>
              </v-row>


              <v-row v-if="subscription.formulas.length > 0">
                <v-col cols="12">
                  <v-select
                      :label="$tc('subscription.formula', 1)"
                      v-model="addLicenceForm.subscriptionFormulaId"
                      :items="subscription.formulas"
                      item-value="id"
                      hide-details
                  >
                    <template #item="{item}">
                      <div>
                        {{ item.name }}
                        <div>
                          <v-chip small v-for="filter in item.filters" :key="filter">{{ $t(`enums.subscription-plan-filters.${filter}`) }}</v-chip>
                          <v-chip small v-for="property in item.properties" :key="property">{{ $t(`enums.subscription-plan-properties.${property}`) }}</v-chip>
                        </div>
                      </div>
                    </template>

                    <template #selection="{item}">
                      <div>
                        {{ item.name }}
                        <div>
                          <v-chip small v-for="filter in item.filters" :key="filter">{{ $t(`enums.subscription-plan-filters.${filter}`) }}</v-chip>
                          <v-chip small v-for="property in item.properties" :key="property">{{ $t(`enums.subscription-plan-properties.${property}`) }}</v-chip>
                        </div>
                      </div>
                    </template>
                  </v-select>
                </v-col>
              </v-row>
              <v-row v-if="subscription.options.length > 0">
                <v-col cols="12">
                  <v-select
                      :label="$tc('subscription.option', 2)"
                      v-model="addLicenceForm.subscriptionOptions"
                      :items="subscription.options"
                      multiple
                      :menu-props="{
                        bottom: true,
                        'offset-y': true,
                      }"
                      item-value="id"
                      hide-details
                  >
                    <template #item="{item}">
                      <div>
                        {{ item.name }}
                        <div>
                          <v-chip small v-for="property in item.properties" :key="property">{{ $t(`enums.subscription-plan-properties.${property}`) }}</v-chip>
                        </div>
                      </div>
                    </template>

                    <template #selection="{item}">
                      <div>
                        {{ item.name }}
                        <div>
                          <v-chip small v-for="property in item.properties" :key="property">{{ $t(`enums.subscription-plan-properties.${property}`) }}</v-chip>
                        </div>
                      </div>
                    </template>
                  </v-select>
                </v-col>
              </v-row>


              <v-row>
                <v-col cols="12" :md="canSelectStartBillingType && canShowStartBillingDate ? 6 : 12" v-if="canSelectStartBillingType">
                  <v-select
                      :label="$t('subscription.start-billing-at')"
                      v-model="addLicenceForm.startBillingAt"
                      :items="billingStartDateAtItems"
                      hide-details
                  />
                </v-col>
                <v-col cols="12" :md="canSelectStartBillingType ? 6 : 12" v-if="canShowStartBillingDate">
                  <date-picker
                      :label="$t('subscription.billing-start-date')"
                      v-model="addLicenceForm.billingStartDate"
                      clearable
                      :disabled="!canSetStartBillingDate"
                      hide-details
                  />
                </v-col>
                <!-- La licence est souvent créée avant l'installation de la borne : on ne connaît
                     pas encore la date. On l'annonce plutôt que de laisser un champ vide sans
                     explication. -->
                <v-col cols="12" v-if="awaitsFirstChargeForBilling">
                  <p class="first-charge-hint">
                    <v-icon x-small left>mdi-clock-outline</v-icon>{{ $t('subscription.first-charge-pending-hint') }}
                  </p>
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="12" :md="addLicenceForm.startActivationAt === 'custom' ? 6 : 12">
                  <v-select
                      :label="$t('subscription.activation-start-date')"
                      v-model="addLicenceForm.startActivationAt"
                      :items="activationStartDateAtItems"
                      hide-details
                  />
                </v-col>
                <v-col cols="12" md="6" v-if="addLicenceForm.startActivationAt === 'custom'">
                  <date-picker
                      :label="$t('subscription.activation-start-date')"
                      v-model="addLicenceForm.activationStartDate"
                      clearable
                      hide-details
                  />
                </v-col>
                <v-col cols="12" v-if="awaitsFirstChargeForActivation">
                  <p class="first-charge-hint">
                    <v-icon x-small left>mdi-clock-outline</v-icon>{{ $t('subscription.first-charge-pending-hint') }}
                  </p>
                </v-col>
              </v-row>

              <v-row v-if="isEditLicence" dense>
                <v-col cols="12">
                  <v-switch
                      :label="$t('subscription.is-exempt-from-billing')"
                      v-model="addLicenceForm.isExemptFromBilling"
                  />
                </v-col>
                <v-col cols="12" v-if="addLicenceForm.isExemptFromBilling">
                  <v-textarea
                      :label="$t('subscription.exempt-from-billing-reason')"
                      v-model="addLicenceForm.exemptFromBillingReason"
                      rows="2"
                      auto-grow
                      :rules="[commonRules.required]"
                  />
                </v-col>
              </v-row>

              <template v-if="['energy-provider'].includes(subscription.licenceType)">
                <v-row>
                  <v-col cols="12" md="6">
                    <v-select
                        v-model="billingReceiptType"
                        :items="[
                          {text: $tc('member.member'), value: 'member'},
                          {text: $t('organization.organization'), value: 'organization'},
                        ]"
                        :label="$t('subscription.billing-type')"
                    />
                  </v-col>
                  <v-col cols="12" md="6" v-if="billingReceiptType === 'organization'">
                    <organization-select
                        :label="$t('organization.organization')"
                        v-model="addLicenceForm.billingOrganizationId"
                        selection-with-no-extra
                        :rules="[commonRules.required]"
                        instanceId="subscriptionBilling"
                    />
                  </v-col>
                  <v-col cols="12" md="6"  v-if="billingReceiptType === 'member'">
                    <member-select
                        :label="$tc('member.member')"
                        :organization-id="subscription.organizationId"
                        v-model="addLicenceForm.billingMemberId"
                        selection-with-no-extra
                        :rules="[commonRules.required]"
                    />
                  </v-col>
                </v-row>

                <v-row dense>
                  <v-col cols="12">
                    <v-switch
                        :label="$t('subscription.split-consumption-billing')"
                        v-model="consumptionSplit"
                        hide-details
                    />
                  </v-col>
                </v-row>
                <v-row v-if="consumptionSplit">
                  <v-col cols="12" md="6">
                    <v-select
                        v-model="consumptionBillingReceiptType"
                        :items="[
                          {text: $tc('member.member'), value: 'member'},
                          {text: $t('organization.organization'), value: 'organization'},
                        ]"
                        :label="$t('subscription.consumption-billing-type')"
                    />
                  </v-col>
                  <v-col cols="12" md="6" v-if="consumptionBillingReceiptType === 'organization'">
                    <organization-select
                        :label="$t('organization.organization')"
                        v-model="addLicenceForm.consumptionBillingOrganizationId"
                        selection-with-no-extra
                        :rules="[commonRules.required]"
                        instanceId="consumptionBilling"
                    />
                  </v-col>
                  <v-col cols="12" md="6" v-if="consumptionBillingReceiptType === 'member'">
                    <member-select
                        :label="$tc('member.member')"
                        :organization-id="subscription.organizationId"
                        v-model="addLicenceForm.consumptionBillingMemberId"
                        selection-with-no-extra
                        :rules="[commonRules.required]"
                    />
                  </v-col>
                </v-row>
              </template>

              <!-- Extras Section -->
              <v-row dense class="mt-4">
                <v-col cols="12">
                  <v-divider></v-divider>
                  <div class="d-flex justify-space-between align-center mt-4 mb-2">
                    <h3>{{ $t('subscription.extra-reimbursement') }}</h3>
                    <v-btn small  @click="addReimbursementExtra">
                      <v-icon left small>mdi-plus</v-icon>
                      {{ $t('subscription.extra-add-reimbursement') }}
                    </v-btn>
                  </div>
                </v-col>
              </v-row>

              <v-row dense v-for="(reimbursement, index) in extraCostReimbursements" :key="'reimbursement-' + index" class="mb-2">
                <v-col cols="12" md="4">
                  <v-select
                      :label="$t('subscription.extra-reimbursement-type')"
                      v-model="reimbursement.type"
                      :items="reimbursementTypeOptions"
                      item-value="value"
                      item-text="text"
                      hide-details
                      dense
                  />
                </v-col>
                <v-col cols="12" md="3" v-if="reimbursement.type === 'custom'">
                  <v-text-field
                      :label="$t('subscription.extra-reimbursement-custom-label')"
                      v-model="reimbursement.label"
                      hide-details
                      dense
                  />
                </v-col>
                <v-col cols="12" :md="reimbursement.type === 'custom' ? 4 : 7">
                  <v-text-field
                      type="number"
                      :min="0"
                      step="0.01"
                      :label="$t('subscription.extra-reimbursement-price')"
                      v-model.number="reimbursement.price"
                      hide-details
                      dense
                  >
                    <template #append>
                      <div class="pt-3">€</div>
                    </template>
                  </v-text-field>
                </v-col>
                <v-col cols="12" md="1" class="d-flex align-center">
                  <v-btn icon small @click="removeReimbursementExtra(index)">
                    <v-icon small color="red">mdi-delete</v-icon>
                  </v-btn>
                </v-col>
              </v-row>

              <v-row dense class="mt-4">
                <v-col cols="12">
                  <v-divider></v-divider>
                  <div class="d-flex justify-space-between align-center mt-4 mb-2">
                    <h3>{{ $t('subscription.extra-fees') }}</h3>
                    <v-btn small @click="addFeesExtra">
                      <v-icon left small>mdi-plus</v-icon>
                      {{ $t('subscription.extra-add-fees') }}
                    </v-btn>
                  </div>
                </v-col>
              </v-row>

              <v-row dense v-for="(fees, index) in extraAdditionalCosts" :key="'fees-' + index" class="mb-2">
                <v-col cols="12" md="4">
                  <v-select
                      :label="$t('subscription.extra-fees-type')"
                      v-model="fees.type"
                      :items="feesTypeOptions"
                      item-value="value"
                      item-text="text"
                      hide-details
                      dense
                  />
                </v-col>
                <v-col cols="12" md="3" v-if="fees.type === 'custom'">
                  <v-text-field
                      :label="$t('subscription.extra-fees-custom-label')"
                      v-model="fees.label"
                      hide-details
                      dense
                  />
                </v-col>
                <v-col cols="12" :md="fees.type === 'custom' ? 4 : 7">
                  <v-text-field
                      type="number"
                      :min="0"
                      step="0.01"
                      :label="$t('subscription.extra-fees-price')"
                      v-model.number="fees.price"
                      hide-details
                      dense
                  >
                    <template #append>
                      <div class="pt-3">€</div>
                    </template>
                  </v-text-field>
                </v-col>
                <v-col cols="12" md="1" class="d-flex align-center">
                  <v-btn icon small @click="removeFeesExtra(index)">
                    <v-icon small color="red">mdi-delete</v-icon>
                  </v-btn>
                </v-col>
              </v-row>

            </template>
          </v-card-text>

          <v-divider class="mt-6"></v-divider>

          <v-card-actions>
            <v-row class="ma-0">
              <v-col class="text-left" v-if="addLicenceForm?.id">
                <v-btn class="mr-3" small @click="openUnsubscribeModal">{{ addLicenceForm.unsubscribedAt ? $t('subscription.edit-terminate-date') : $t('subscription.terminate-a-licence') }}</v-btn>
              </v-col>
              <v-col class="text-right">
                <v-btn class="mr-3" small @click="closeLicenceFormModal">{{ $t('cancel') }}</v-btn>
                <v-btn small color="primary" type="submit" :disabled="!canSaveLicence || loadingSaveLicence" :loading="loadingSaveLicence">{{ $t(isEditLicence ? 'update' : 'add') }}</v-btn>
              </v-col>
            </v-row>
          </v-card-actions>
        </v-card>
      </v-form>
    </v-dialog>

  </div>
</template>

<script>
import AuditTrailButton from "@elements/AuditTrailButton.vue";
import SubscriptionRepository from "@repository/SubscriptionRepository";
import ChargingPointSelect from "@blocks/Select/ChargingPointSelect.vue";
import OrganizationSelect from "@blocks/Select/OrganizationSelect.vue";
import dayjs from "dayjs";
import MemberSelect from "@blocks/Select/MemberSelect.vue";
import RulesMixin from "@mixins/RulesMixin";
import MeterDeviceSelect from "@blocks/Select/MeterDeviceSelect.vue";
import VehicleSelect from "@blocks/Select/VehicleSelect.vue";

export default {
  components: {
    AuditTrailButton,
    OrganizationSelect,
    ChargingPointSelect,
    MemberSelect,
    MeterDeviceSelect,
    VehicleSelect,
  },
  data() {
    return {
      search: "",
      showActiveLicence: true,
      licences: [],
      loading: false,
      billingReceiptType: 'member',
      consumptionSplit: false,
      consumptionBillingReceiptType: 'member',
      subscriptionStruct: null,
      showUnsubscribeModal: false,
      unsubscribeDate: null,
      unsubscribeLicenceId: null,
      unsubscribeIsModify: false,
      showLicenceFormModal: false,
      selectedLicence: null,
      addLicenceForm: null,
      chargingPointIds: [],
      meterDeviceIds: [],
      loadingSaveLicence: false,
      selectedDeviceType: 'charging-point',
      // Extras data - arrays for multiple entries
      extraCostReimbursements: [],
      extraAdditionalCosts: [],
    }
  },

  mixins: [
    RulesMixin,
  ],

  props: {
    subscriptionId: String,
    organizationId: String,
    memberId: String,
    chargingPointId: String,
    meterDeviceId: String,
    vehicleId: String,
  },

  mounted() {
    this.loadSubscriptionStruct()
    this.loadData()
  },

  watch: {
    subscriptionId() {
      this.loadData()
    },
    organizationId() {
      // The entity (vehicle/meter) loads async, so the org arrives after mount — reload the subscription
      // struct too (the "add licence" dropdown), else it stays unfiltered and lists every org's subscriptions.
      this.loadSubscriptionStruct()
      this.loadData()
    },
    'addLicenceForm.subscriptionId'() {
      if(!this.isEditLicence) {
        this.addLicenceForm.subscriptionFormulaId = this.subscription.formulas?.length > 0 ? this.subscription.formulas[0].id : null
        this.addLicenceForm.subscriptionOptions = []
      }
    },

    billingReceiptType(type) {
      if(type === 'member') {
        this.addLicenceForm.billingOrganizationId = null
      } else {
        this.addLicenceForm.billingMemberId = null
      }
    },

    consumptionSplit(enabled) {
      if(!enabled) {
        this.addLicenceForm.consumptionBillingOrganizationId = null
        this.addLicenceForm.consumptionBillingMemberId = null
      }
    },

    consumptionBillingReceiptType(type) {
      if(type === 'member') {
        this.addLicenceForm.consumptionBillingOrganizationId = null
      } else {
        this.addLicenceForm.consumptionBillingMemberId = null
      }
    },

    /**
     * Sets the billing start date for the addLicenceForm based on the startBillingAt value and subscription billing cycle.
     *
     * @return {void}
     */
    'addLicenceForm.billingStartDate'(val) {
      if (this.addLicenceForm.startActivationAt === 'same-as-billing') {
        this.addLicenceForm.activationStartDate = val
      }
    },

    'addLicenceForm.startActivationAt'(val) {
      this.addLicenceForm.activationStartDateAsFirstChargeDate = false
      if (val === 'same-as-billing') {
        this.addLicenceForm.activationStartDate = this.addLicenceForm.billingStartDate
      } else if (val === 'immediately') {
        this.addLicenceForm.activationStartDate = dayjs().format('YYYY-MM-DD')
      } else if (val === 'next-period') {
        this.addLicenceForm.activationStartDate = dayjs().add(1, 'month').startOf('month').format('YYYY-MM-DD')
      } else if (val === 'first-charge-date') {
        this.addLicenceForm.activationStartDate = null
        this.addLicenceForm.activationStartDateAsFirstChargeDate = true
      }
    },

    'addLicenceForm.startBillingAt'() {
      this.addLicenceForm.billingStartDateAsFirstChargeDate = false
      if(this.addLicenceForm.startBillingAt === 'immediately') {
        this.addLicenceForm.billingStartDate = dayjs().format('YYYY-MM-DD')
      } else if(this.addLicenceForm.startBillingAt === 'next-period') {

        this.addLicenceForm.billingStartDate = dayjs().add(1, 'month').startOf("month").format('YYYY-MM-DD')

        // If billing depend on the subscription billing cycle ?

        // let currentYear = dayjs().year();
        // let currentMonth = dayjs().month()
        // if(this.subscription.billingCycle === 'ANNUALLY') {
        //   this.addLicenceForm.billingStartDate = dayjs().add(1, 'year').startOf("year").format('YYYY-MM-DD')
        // } else if(this.subscription.billingCycle === 'BIANNUAL') {
        //   if(currentMonth < 6) {
        //     this.addLicenceForm.billingStartDate = currentYear + '-07-01'
        //   } else {
        //     this.addLicenceForm.billingStartDate = dayjs().add(1, 'year').startOf("year").format('YYYY-MM-DD')
        //   }
        // } else if(this.subscription.billingCycle === 'MONTHLY') {
        //   this.addLicenceForm.billingStartDate = dayjs().add(1, 'month').startOf("month").format('YYYY-MM-DD')
        // } else if(this.subscription.billingCycle === 'QUARTERLY') {
        //   if(currentMonth < 3) {
        //     this.addLicenceForm.billingStartDate = currentYear + '-04-01'
        //   } else if(currentMonth < 6) {
        //     this.addLicenceForm.billingStartDate = currentYear + '-07-01'
        //   } else if(currentMonth < 9) {
        //     this.addLicenceForm.billingStartDate = currentYear + '-10-01'
        //   } else {
        //     this.addLicenceForm.billingStartDate = dayjs().add(1, 'year').startOf("year").format('YYYY-MM-DD')
        //   }
        // }
      } else if(this.addLicenceForm.startBillingAt === 'first-charge-date') {
        this.addLicenceForm.billingStartDate = null
        this.addLicenceForm.billingStartDateAsFirstChargeDate = true
      } else if(this.addLicenceForm.startBillingAt === 'exempt-from-billing') {
        this.addLicenceForm.billingStartDate = null
        this.addLicenceForm.isExemptFromBilling = true
      }
    },

    selectedDeviceType() {
      this.chargingPointIds = []
      this.meterDeviceIds = []
    },
    'addLicenceForm.subscriptionFormulaId'() {
      // A different formula may constrain the meter type differently → drop a now-possibly-invalid pick.
      this.meterDeviceIds = []
    }
  },

  computed: {
    reimbursementTypeOptions() {
      return [
        {text: this.$t('subscription.extra-type-subscription-price'), value: 'subscription-price'},
        {text: this.$t('subscription.extra-type-custom'), value: 'custom'},
      ]
    },

    feesTypeOptions() {
      const options = []

      // Add equipment-rental if the licence has this property
      if(this.hasEquipmentRentalProperty(this.addLicenceForm)) {
        options.push({text: this.$t('subscription.extra-type-equipment-rental'), value: 'equipment-rental'})
      }

      options.push({text: this.$t('subscription.extra-type-custom'), value: 'custom'})

      return options
    },

    /**
     * Seule une licence portant sur une BORNE peut démarrer à sa première recharge.
     *
     * En ajout, c'est le bouton radio qui le dit. En édition ce radio est masqué et garde la
     * dernière valeur choisie dans un ajout précédent — il ne décrit pas la licence qu'on
     * modifie. D'où l'option qui disparaissait : on interrogeait un état de formulaire au lieu
     * de la licence elle-même.
     */
    /**
     * La mention n'est affichée que sur le VERDICT DU SERVEUR, jamais sur la simple sélection
     * du mode : au moment où l'utilisateur ouvre le déroulant, le front ne sait pas s'il existe
     * une charge, et annoncer « aucune charge n'a eu lieu » serait faux une fois sur deux.
     *
     * À l'enregistrement, le back cherche la première charge éligible : s'il en trouve une il
     * pose la date (et le drapeau retombe), sinon la licence reste en attente et ces drapeaux
     * remontent à la réouverture du formulaire.
     */
    awaitsFirstChargeForActivation() {
      return this.addLicenceForm?.startActivationAt === 'first-charge-date'
        && this.addLicenceForm?.pendingActivationFirstCharge === true
    },

    awaitsFirstChargeForBilling() {
      return this.addLicenceForm?.startBillingAt === 'first-charge-date'
        && this.addLicenceForm?.pendingBillingFirstCharge === true
    },

    licenceTargetsChargingPoint() {
      if (this.isEditLicence) {
        return this.addLicenceForm?.subjectType === 'charging-point'
      }
      return this.selectedDeviceType === 'charging-point'
    },

    activationStartDateAtItems() {
      const items = [
        {text: this.$t('subscription.same-as-billing-start'), value: 'same-as-billing'},
        {text: this.$t('subscription.immediately'), value: 'immediately'},
        {text: this.$t('subscription.next-period'), value: 'next-period'},
        {text: this.$t('subscription.custom-date'), value: 'custom'},
      ]

      if(['device', 'charging-point', 'energy-provider'].includes(this.subscription.licenceType) && this.licenceTargetsChargingPoint) {
        items.push({text: this.$t('subscription.first-charge-date'), value: 'first-charge-date'})
      }

      return items
    },

    billingStartDateAtItems() {
      const items = [
        {text: this.$t('subscription.immediately'), value: 'immediately'},
        {text: this.$t('subscription.next-period'), value: 'next-period'},
        {text: this.$t('subscription.custom-date'), value: 'custom'},
      ]

      if(['device', 'charging-point', 'energy-provider'].includes(this.subscription.licenceType) && this.licenceTargetsChargingPoint) {
        items.push({text: this.$t('subscription.first-charge-date'), value: 'first-charge-date'})
      }

      items.push({text: this.$t('subscription.is-exempt-from-billing'), value: 'exempt-from-billing'})

      return items
    },

    headers() {
      if(this.showActiveLicence) {
        const headers = [
          { text: this.$t('name'), value: 'related.label', align: 'left'},
          { text: this.$tc('subscription.subscription'), value: 'name', },
          { text: this.$t('since'), value: 'activationStartDate' },
          { text: this.$t('subscription.scheduled-termination'), value: 'unsubscribedAt', sortable: false },
          { text: this.$t('subscription.billing-start-date'), value: 'billingStartDate' },
          { text: this.$t('configuration'), value: 'config', sortable: false, align: 'left'},
          { text: this.$t('price-ht'), value: 'price' },
          { text: '', value: 'actions', width: 200, sortable: false, },
        ]

        if(!this.organizationId && !this.subscriptionId) {
          headers.splice(0, 0, { text: this.$t('organization.organization'), value: 'organization.name', sortable: false, })
        }
        return headers
      } else {
        const headers = [
          { text: this.$t('name'), value: 'related.label', align: 'left'},
          { text: this.$tc('subscription.subscription'), value: 'name', },
          { text: this.$t('since'), value: 'activationStartDate' },
          { text: this.$t('subscription.billing-start-date'), value: 'billingStartDate' },
          { text: this.$t('subscription.unsubscribed-at'), value: 'unsubscribedAt' },
          { text: this.$t('configuration'), value: 'config', sortable: false, align: 'left'},
          { text: this.$t('price-ht'), value: 'price'},
          { text: '', value: 'actions', width: 200, sortable: false, },
        ]

        if(!this.organizationId && !this.subscriptionId) {
          headers.splice(0, 0, { text: this.$t('organization.organization'), value: 'organization.name', sortable: false, })
        }
        return headers
      }


    },

    existingChargingPointIds() {
      let chargingPointIdBySubscriptionId = {}
      for(let licence of this.licences) {
        if(licence.active) {
          if (licence.related?.type === 'charging-point') {
            if (!chargingPointIdBySubscriptionId[licence.subscription.id]) {
              chargingPointIdBySubscriptionId[licence.subscription.id] = []
            }
            chargingPointIdBySubscriptionId[licence.subscription.id].push(licence.related.id)
          }
        }
      }

      return chargingPointIdBySubscriptionId
    },



    excludeChargingPointIds() {
      return this.existingChargingPointIds[this.addLicenceForm.subscriptionId]
    },

    existingMeterDeviceIds() {
      let meterDeviceIdBySubscriptionId = {}
      for(let licence of this.licences) {
        if(licence.active) {
          if(licence.related?.type === 'meter-device') {
            if(!meterDeviceIdBySubscriptionId[licence.subscription.id]) {
              meterDeviceIdBySubscriptionId[licence.subscription.id] = []
            }
            meterDeviceIdBySubscriptionId[licence.subscription.id].push(licence.related.id)
          }
        }
      }

      return meterDeviceIdBySubscriptionId
    },

    existingVehicleIds() {
      let vehicleIdBySubscriptionId = {}
      for(let licence of this.licences) {
        if(licence.active) {
          if(licence.related?.type === 'vehicle') {
            if(!vehicleIdBySubscriptionId[licence.subscription.id]) {
              vehicleIdBySubscriptionId[licence.subscription.id] = []
            }
            vehicleIdBySubscriptionId[licence.subscription.id].push(licence.related.id)
          }
        }
      }

      return vehicleIdBySubscriptionId
    },

    excludeMeterDeviceIds() {
      return this.existingMeterDeviceIds[this.addLicenceForm.subscriptionId]
    },

    excludeVehicleIds() {
      return this.existingVehicleIds[this.addLicenceForm.subscriptionId]
    },

    subscriptions() {
      return this.subscriptionStruct?.filter(s => {
        if(this.chargingPointId) {
          return ['charging-point', 'device'].includes(s.licenceType)
        }
        // Une licence de compteur : abonnement d'équipements (le type « borne » n'en porte pas).
        if(this.meterDeviceId) {
          return ['device', 'energy-provider'].includes(s.licenceType)
        }
        if(this.memberId) {
          return ['member'].includes(s.licenceType)
        }
        return true
      })
    },

    licenceInactivesCount() {
      return this.licences.filter(l => !l.active).length
    },

    licencesFiltered() {
      let search = this.search?.trim().toLowerCase() ?? ""
      let licences = this.licences.filter(l => l.active === this.showActiveLicence)

      if(search) {
        licences = licences.filter(l => l.related?.label.toLowerCase().includes(search))
      }
      return licences
    },

    canSaveLicence() {
      if(!this.subscription) {
        return false
      }



      if(this.subscription.formulas.length > 0 && !this.addLicenceForm.subscriptionFormulaId) {
        return false
      }


      if(this.subscription.licenceType === 'energy-provider') {
        if(this.billingReceiptType === 'member' && !this.addLicenceForm.billingMemberId) {
          return false
        }
        if(this.billingReceiptType === 'organization' && !this.addLicenceForm.billingOrganizationId) {
          return false
        }
        if(this.consumptionSplit) {
          if(this.consumptionBillingReceiptType === 'member' && !this.addLicenceForm.consumptionBillingMemberId) {
            return false
          }
          if(this.consumptionBillingReceiptType === 'organization' && !this.addLicenceForm.consumptionBillingOrganizationId) {
            return false
          }
        }
      }

      if(this.isEditLicence) {
        if (this.addLicenceForm.quantity < 0) {
          return false
        }

        return true
      }

      if(['device', 'charging-point', 'energy-provider'].includes(this.subscription.licenceType)) {
        return this.chargingPointIds.length > 0 || this.meterDeviceIds.length > 0
      }
      if(['organization'].includes(this.subscription.licenceType)) {
        return !!this.addLicenceForm.organizationId
      }
      if(['member'].includes(this.subscription.licenceType)) {
        return !!this.addLicenceForm.memberId
      }
      if(this.subscription.licenceType === 'vehicle') {
        return !!this.addLicenceForm.vehicleId
      }

      return false
    },

    subscription() {
      return this.subscriptionStruct?.find(s => s.id === this.addLicenceForm?.subscriptionId)
    },

    // The device-type filters (meter types only) of the selected formula → constrains the meter selector so
    // a device-scoped formula (e.g. connected-vehicle) only offers matching meters. Empty = no constraint.
    selectedFormulaMeterTypes() {
      const formula = this.subscription?.formulas?.find(f => f.id === this.addLicenceForm?.subscriptionFormulaId)
      const meterTypes = ['connected-plug', 'smart-cable', 'mid-meter', 'external-operator', 'connected-vehicle']
      return (formula?.filters || []).filter(f => meterTypes.includes(f))
    },

    meterSelectFilterQuery() {
      const q = { organizationId: this.subscription?.organizationId }
      if (this.selectedFormulaMeterTypes.length) q.types = this.selectedFormulaMeterTypes
      return q
    },

    isEditLicence() {
      return !!this.addLicenceForm?.id
    },

    // Modifiable en édition aussi : le back accepte désormais les mêmes modes qu'à la
    // création, donc « date de première recharge » a de nouveau une valeur à envoyer. C'est
    // le cas d'usage réel — la licence est configurée avant l'installation de la borne, et on
    // ne connaît pas encore la date de mise en service.
    canSelectStartBillingType() {
      return true
    },

    canShowStartBillingDate() {
      return (this.isEditLicence && !this.addLicenceForm.isExemptFromBilling) || ['custom', 'next-period'].includes(this.addLicenceForm.startBillingAt)
    },

    canSetStartBillingDate() {
      return this.isEditLicence || ['custom'].includes(this.addLicenceForm.startBillingAt)
    }

  },

  methods: {
    loadData() {

      this.loading = true
      SubscriptionRepository.searchLicences({
        organizationId: this.organizationId,
        subscriptionId: this.subscriptionId,
        memberId: this.memberId,
        chargingPointId: this.chargingPointId,
        vehicleId: this.vehicleId,
        meterDeviceId: this.meterDeviceId,
      })
      .then(response => {
          this.licences = response
          this.loading = false
          if(!this.showActiveLicence && this.licenceInactivesCount === 0) {
            this.showActiveLicence = true
          }
      }).catch(error => {
          this.loading = false
          this.notifyError(error)
      })
    },


    openAddLicenceForm() {
      this.addLicenceForm = {
        subscriptionId: null,
        // Declared up-front (not assigned later) so Vue 2 tracks them reactively — otherwise changing the
        // formula never recomputed selectedFormulaMeterTypes and the meter picker stayed on the first
        // formula's filter (mid-meter).
        subscriptionFormulaId: null,
        subscriptionOptions: [],
        startActivationAt: 'same-as-billing',
        activationStartDate: dayjs().format('YYYY-MM-DD'),
        activationStartDateAsFirstChargeDate: false,
        startBillingAt: 'immediately',
        billingStartDateAsFirstChargeDate: false,
        // Aucun verdict serveur sur une licence qui n'existe pas encore : pas de mention tant
        // qu'on n'a pas enregistré.
        pendingActivationFirstCharge: false,
        pendingBillingFirstCharge: false,
        isExemptFromBilling: false,
        quantity: 1,
        costReimbursements: [],
        additionalCosts: [],
        consumptionBillingOrganizationId: null,
        consumptionBillingMemberId: null,
      }
      this.consumptionSplit = false
      this.consumptionBillingReceiptType = 'member'
      if(this.subscriptionId) {
        this.addLicenceForm.subscriptionId = this.subscriptionId
      } else if (this.subscriptionStruct?.length > 0) {
        this.addLicenceForm.subscriptionId =  this.subscriptionStruct[0].id
      }

      this.addLicenceForm.subscriptionFormulaId = this.subscription.formulas?.length > 0 ? this.subscription.formulas[0].id : null
      this.addLicenceForm.subscriptionOptions = []

      // Ouvert depuis une fiche : son équipement, de son type. Posé après les observateurs (type, formule),
      // qui vident la sélection quand ils se déclenchent.
      this.selectedDeviceType = this.meterDeviceId ? 'meter-device' : 'charging-point'
      this.chargingPointIds = []
      this.meterDeviceIds = []
      this.$nextTick(() => {
        this.chargingPointIds = this.chargingPointId ? [this.chargingPointId] : []
        this.meterDeviceIds = this.meterDeviceId ? [this.meterDeviceId] : []
      })

      // Reset extras arrays
      this.extraCostReimbursements = []
      this.extraAdditionalCosts = []

      this.showLicenceFormModal = true
    },

    openEditLicenceForm(item) {
      const licence = this.cloneObj(item)

      this.addLicenceForm = {
        id: licence.id,
        subscriptionId: licence.subscription?.id,
        subscriptionFormulaId: licence.formula?.id,
        subscriptionOptions: licence.options?.map(o => o.id),
        related: licence.related,
        subscribedAt: licence.subscribedAt,
        unsubscribedAt: licence.unsubscribedAt,
        // Repart de l'état réel : une licence qui attend sa première recharge n'a pas de date,
        // et l'afficher en « date personnalisée » vide effaçait silencieusement son mode à
        // l'enregistrement suivant.
        subjectType: licence.subjectType,
        // Verdict du serveur, en lecture seule : les champs *AsFirstChargeDate ci-dessous sont
        // des paramètres de commande que les watchers réécrivent au moindre changement de
        // déroulant, ils ne peuvent donc pas servir à savoir ce que le serveur a constaté.
        pendingActivationFirstCharge: !!licence.activationStartDateAwaitsFirstCharge,
        pendingBillingFirstCharge: !!licence.billingStartDateAwaitsFirstCharge,
        startActivationAt: licence.activationStartDateAwaitsFirstCharge ? 'first-charge-date' : 'custom',
        activationStartDate: licence.activationStartDate,
        activationStartDateAsFirstChargeDate: !!licence.activationStartDateAwaitsFirstCharge,
        startBillingAt: licence.billingStartDateAwaitsFirstCharge ? 'first-charge-date' : 'custom',
        billingStartDateAsFirstChargeDate: !!licence.billingStartDateAwaitsFirstCharge,
        billingStartDate: licence.billingStartDate,
        active: licence.active,
        quantity: licence.quantity,
        isExemptFromBilling: licence.isExemptFromBilling,
        exemptFromBillingReason: licence.exemptFromBillingReason ?? null,
        costReimbursements: licence.costReimbursements || [],
        additionalCosts: licence.additionalCosts || [],
        consumptionBillingOrganizationId: null,
        consumptionBillingMemberId: null,
      }

      // Load cost reimbursement extras (all entries)
      this.extraCostReimbursements = (licence.costReimbursements || []).map(r => ({
        type: r.type,
        label: r.label || '',
        price: r.price
      }))

      // Load additional costs extras (all entries)
      this.extraAdditionalCosts = (licence.additionalCosts || []).map(f => ({
        type: f.type,
        label: f.label || '',
        price: f.price
      }))

      if(licence.related) {
        if(licence.related.type === 'charging-point') {
          this.addLicenceForm.chargingPointId = licence.related.id
        }
        if(licence.related.type === 'member') {
          this.addLicenceForm.memberId = licence.related.id
        }
        if(licence.related.type === 'organization') {
          this.addLicenceForm.organizationId = licence.related.id
        }
        if(licence.related.type === 'vehicle') {
          this.addLicenceForm.vehicleId = licence.related.id
        }
      }
      if(licence.subscriptionPlan.licenceType === 'energy-provider') {
        if(licence.billingMemberId) {
          this.billingReceiptType = 'member'
          this.addLicenceForm.billingMemberId = licence.billingMemberId
        } else if(licence.billingOrganizationId) {
          this.billingReceiptType = 'organization'
          this.addLicenceForm.billingOrganizationId = licence.billingOrganizationId
        }
        if(licence.consumptionBillingMemberId) {
          this.consumptionSplit = true
          this.consumptionBillingReceiptType = 'member'
          this.addLicenceForm.consumptionBillingMemberId = licence.consumptionBillingMemberId
        } else if(licence.consumptionBillingOrganizationId) {
          this.consumptionSplit = true
          this.consumptionBillingReceiptType = 'organization'
          this.addLicenceForm.consumptionBillingOrganizationId = licence.consumptionBillingOrganizationId
        } else {
          this.consumptionSplit = false
        }
      }
      this.showLicenceFormModal = true
    },

    unsubscribe(id, date) {
      this.$dialog.confirm({text: this.$t('are-you-sure')}).then(result => {
        if(result) {
          this.showLoading(true)
          SubscriptionRepository.unregisterLicence(id, date).then(result => {
            this.notifySuccess(this.$t('subscription.licence-terminated'))
            this.showLoading(false)
            this.loadData()
            this.showUnsubscribeModal = false
            this.closeLicenceFormModal()
          }).catch(error => {
            this.showLoading(false)
            this.notifyError(error)
          })
        }
      })
    },



    cancelUnsubscription(item) {
      this.$dialog.confirm({text: this.$t('are-you-sure')}).then(result => {
        if(result) {
          this.showLoading(true)
          SubscriptionRepository.reactivateLicence(item.id).then(result => {
            this.showLoading(false)
            this.loadData()
          }).catch(error => {
            this.showLoading(false)
            this.notifyError(error)
          })
        }
      })
    },

    closeLicenceFormModal() {
      this.showLicenceFormModal = false
    },

    openUnsubscribeModal() {
      this.unsubscribeLicenceId = this.addLicenceForm.id
      this.unsubscribeIsModify = !!this.addLicenceForm.unsubscribedAt
      this.unsubscribeDate = this.addLicenceForm.unsubscribedAt
        ? dayjs(this.addLicenceForm.unsubscribedAt).format('YYYY-MM-DD')
        : dayjs().format('YYYY-MM-DD')
      this.showUnsubscribeModal = true
    },

    // Modifier la date de résiliation directement depuis la liste (licence active déjà programmée).
    openUnsubscribeModalFor(item) {
      this.unsubscribeLicenceId = item.id
      this.unsubscribeIsModify = !!item.unsubscribedAt
      this.unsubscribeDate = item.unsubscribedAt
        ? dayjs(item.unsubscribedAt).format('YYYY-MM-DD')
        : dayjs().format('YYYY-MM-DD')
      this.showUnsubscribeModal = true
    },

    addReimbursementExtra() {
      this.extraCostReimbursements.push({
        type: 'subscription-price',
        label: '',
        price: null
      })
    },

    removeReimbursementExtra(index) {
      this.extraCostReimbursements.splice(index, 1)
    },

    addFeesExtra() {
      // Default to equipment-rental if available, otherwise custom
      const defaultType = this.hasEquipmentRentalProperty(this.addLicenceForm) ? 'equipment-rental' : 'custom'
      this.extraAdditionalCosts.push({
        type: defaultType,
        label: '',
        price: null
      })
    },

    removeFeesExtra(index) {
      this.extraAdditionalCosts.splice(index, 1)
    },

    buildExtrasFromForm() {
      // Build cost reimbursement array - only keep entries with type and price
      const costReimbursements = this.extraCostReimbursements
        .filter(r => r.type && r.price !== null && r.price !== undefined)
        .map(r => ({
          type: r.type,
          label: r.type === 'custom' ? r.label : null,
          price: r.price
        }))

      // Build additional costs array - only keep entries with type and price
      const additionalCosts = this.extraAdditionalCosts
        .filter(f => f.type && f.price !== null && f.price !== undefined)
        .map(f => ({
          type: f.type,
          label: f.type === 'custom' ? f.label : null,
          price: f.price
        }))

      // Update addLicenceForm with separate fields
      this.addLicenceForm.costReimbursements = costReimbursements
      this.addLicenceForm.additionalCosts = additionalCosts
    },

    saveLicence() {
      // Build extras object from form fields
      this.buildExtrasFromForm()

      if(this.$refs.form.validate()) {
        this.loadingSaveLicence = true
        if(this.isEditLicence) {
          SubscriptionRepository.updateLicence(this.addLicenceForm.id, this.addLicenceForm).then(result => {
            this.notifySuccess(this.$t('subscription.licence-added'))
            this.closeLicenceFormModal()
            this.loadingSaveLicence = false
            this.loadData()
          }).catch(error => {
            this.loadingSaveLicence = false
            this.notifyError(error)
          })
        } else {
          const promises = []
          if(this.selectedDeviceType === 'charging-point') {
            for(let chargingPointId of this.chargingPointIds) {
              const data = this.cloneObj(this.addLicenceForm)
              data.chargingPointId = chargingPointId
              let promise = SubscriptionRepository.registerLicence(data).catch(error => {
                this.loadingSaveLicence = false
                return {error}
              })
              promises.push(promise)
            }
          }
          if(this.selectedDeviceType === 'meter-device') {
            for(let meterDeviceId of this.meterDeviceIds) {
              const data = this.cloneObj(this.addLicenceForm)
              data.meterDeviceId = meterDeviceId
              let promise = SubscriptionRepository.registerLicence(data).catch(error => {
                this.loadingSaveLicence = false
                return {error}
              })
              promises.push(promise)
            }
          }
          if(this.subscription?.licenceType === 'vehicle' && this.addLicenceForm.vehicleId) {
            const data = this.cloneObj(this.addLicenceForm)
            let promise = SubscriptionRepository.registerLicence(data).catch(error => {
              this.loadingSaveLicence = false
              return {error}
            })
            promises.push(promise)
          }
          Promise.all(promises).then(results => {
            let errorCount = 0
            for(let result of results) {
              if(result.error) {
                errorCount++
                this.notifyError(result.error)
              } else {
                if (result.params) {
                  this.chargingPointIds = this.chargingPointIds.filter(id => id !== result.params.id)
                  this.$dialog.notify.success(this.$t('subscription.device-licence-added', result.params))
                } else {
                  this.$dialog.notify.success(this.$t('subscription.licence-added'))
                }
              }
            }
            if(errorCount === 0) {
              this.closeLicenceFormModal()
            }
            this.loadingSaveLicence = false
            this.loadData()
          })

        }
      } else {
        this.notifyError(this.$t('form-errors'))
      }
    },

    itemProperties(item) {
      const properties = item.subscriptionPlan.properties?.map(property => ({
        text: this.$t(`enums.subscription-plan-properties.${property}`),
        value: property,
      }))
      if(item.formula) {
        for(let property of item.formula.properties) {
          properties.push({
            text: this.$t(`enums.subscription-plan-properties.${property}`),
            value: property,
          })
        }
      }
      if(item.options?.length > 0) {
        for(let option of item.options) {
          for(let property of option.properties) {
            properties.push({
              text: this.$t(`enums.subscription-plan-properties.${property}`),
              value: property,
            })
          }
        }
      }
      return properties
    },


    itemFilters(item) {
      const filters = item.subscriptionPlan.filters?.map(filter => ({
        text: this.$t(`enums.subscription-plan-filters.${filter}`),
        value: filter,
      }))
      if(item.formula) {
        for(let filter of item.formula.filters) {
          filters.push({
            text: this.$t(`enums.subscription-plan-filters.${filter}`),
            value: filter,
          })
        }
      }
      return filters
    },


    hasEquipmentRentalProperty(licence) {

      const {subscriptionFormulaId, subscriptionOptions} = licence
      const formulasProperties = this.subscription?.formulas?.find(f => f.id === subscriptionFormulaId)?.properties ?? []
      const optionsProperties = [
          ...new Set(
              this.subscription?.options?.filter(f => (subscriptionOptions ?? []).includes(f.id))?.flatMap(option => option.properties ?? []) ?? []
          )
      ];

      return formulasProperties.includes('equipment-rental') || optionsProperties.includes('equipment-rental')
    },


    loadSubscriptionStruct() {

      const data = {}
      if(this.subscriptionId) {
        data.subscriptionId = this.subscriptionId
      } else if(this.organizationId) {
        data.organizationId = this.organizationId
      } else if(this.chargingPointId) {
        data.chargingPointId = this.chargingPointId
      } else if(this.memberId) {
        data.memberId = this.memberId
      }

      SubscriptionRepository.struct(data).then(response => {
        this.subscriptionStruct = response
        this.loading = false
      }).catch(error => {
        this.loading = false
        this.notifyError(error)
      })
    },

  }
}
</script>


<style lang="scss">
.first-charge-hint {
  margin: 0;
  font-size: 12px;
  color: #90a4ae;
  display: flex;
  align-items: center;
}

  .subscription-licences-list {
    .v-select__selections, .v-input__control {
      min-height: 40px;
    }
  }
</style>
